import { useState } from "react";
import { IContractDocuments } from "./ContractDocuments";
import { ContractDocumentRow, DocumentForAddOrEditModel } from "./ContractDocuments.types";
import { useTranslation } from "../../../../localization/useTranslation";
import { EntityType } from "@common/Common.types.ts";
import { isNullOrWhitespace } from "@common/stringOperations.ts";
import { useDotLegalFileUploadDataMapping } from "@common/components/dotLegalFileUploadTable/DotLegalFileUploadTable.hooks.ts";
import { useStateUrlParams } from "@common/hooks/useStateUrlParams.ts";
import { FilterOption, FilterOptionType } from "@common/components/filterPopover/FilterPopover.types.ts";
import { getUniqueFilterOptions, getUniqueNestedFilterOptions, sortByEnumValue, sortByName } from "@common/hooks/filterUtils.ts";
import { DocumentStatus } from "../../../../documents/Documents.types";
import { usePlanContext } from "../../../../auth/planProvider/PlanProvider";
import { useUserContext } from "../../../../auth/userContextProvider/UserContextProvider";
import { useFilterState } from "@common/hooks/useFilterState.ts";
import { applyClientFilters, withOperators } from "@common/components/filterBar/filterOperatorMatching.ts";

import { useDotLegalQuery, useDotLegalQueryWithKey } from "@common/hooks/dotLegalReactQuery";
import { useAiFeatureAccess } from "../../../../plan/aiFeatureGate/useAiFeatureAccess";
import { useGroupEntitiesQuery } from "../../../../processingActivity/hooks/useGroupEntitiesQuery";
import { ContractModel } from "../../Contract.types";
import { getContractQueryKey } from "../../Contract.hooks";
import { IdNamePair } from "../../../../customLists/customListView/CustomListView.types";
import { appliesToAllGroupCompaniesFilterId, matchesGroupCompanyFilter } from "./ContractDocuments.utils";
import { useRefetchWhenPaneSaves, usePanes } from "../../../../main/panes/usePanes";
import DocumentPane from "../../../../main/panes/documentPane/DocumentPane";
import ContractAiUpdatePane from "../../../contractAiUpdate/ContractAiUpdatePane";
import { isPreviewSupported } from "@common/components/documentPreview/DocumentPreview.hooks.ts";
import { useContractAiUpdateAffordance } from "../../../contractAiUpdate/useContractAiUpdateAffordance.ts";
import CreateContractPane from "../../../addContractButton/createContractPane/CreateContractPane";
import { GetContractsPlanLimit } from "../../../../auth/planProvider/PlanProvider.hooks";
import { PreviewableDocument } from "@common/components/documentPreview/DocumentPreview.types.ts";

export function useContractDocumentHooks(props: IContractDocuments) {
    // Obligations created by the AI review in the rail land on another tab, so the only thing to refresh
    // from here is the contract around the tabs, the way the review's onCreated used to.
    useRefetchWhenPaneSaves(() => props.onUpdate?.());

    const { translateString } = useTranslation();
    const { permissions } = useUserContext();
    const { platformFeatures, contractManagementFeatures } = usePlanContext();
    const { showAiQualityAssurance } = useAiFeatureAccess();
    const aiUpdateAffordance = useContractAiUpdateAffordance();
    const canCreateContracts = permissions.contractManagementPermissions.permissions.create;
    const canEditDocuments = permissions.contractManagementPermissions.permissions.edit && !props.readOnly;
    const { contractsPlanLimitData } = GetContractsPlanLimit(aiUpdateAffordance.showContractAiUpdate && canCreateContracts);
    const { open: openPane } = usePanes();
    const { downloadDocumentVersion } = useDotLegalFileUploadDataMapping();
    const [documentForAddOrEdit, setDocumentForAddOrEdit] = useState<DocumentForAddOrEditModel>({ show: false, selectedDocument: undefined });
    const [documentForDeletion, setDocumentForDeletion] = useState<ContractDocumentRow | undefined>(undefined);
    const [downloadingDocument, setDownloadingDocument] = useState("");
    const [documentToSetAsMainDocument, setDocumentToSetAsMainDocument] = useState<ContractDocumentRow | undefined>(undefined);
    const [showAuditLogDialog, setShowAuditLogDialog] = useStateUrlParams("document-auditlog", null);

    const { selectFilters, booleanFilters, filterOperators, onFilterChange, pinnedFilterIds, onPinnedFilterIdsChange } = useFilterState(
        `contract-${props.contractId}-documents-filters`
    );

    const url = `/contract/${props.contractId}/documents`;
    const { isLoading, data: rawData, refetch: refetchDocuments } = useDotLegalQuery<Array<ContractDocumentRow>>(url);

    const { data: contract } = useDotLegalQueryWithKey<ContractModel>(getContractQueryKey(props.contractId), `/contracts/${props.contractId}`);
    const { allGroupEntities } = useGroupEntitiesQuery();

    const documents = filterDocuments(rawData) ?? [];

    const onDownloadClick = async (documentId: string, dataId: string, fileName: string, link?: string) => {
        if (!isNullOrWhitespace(fileName)) {
            setDownloadingDocument(documentId);
            await downloadDocumentVersion(documentId, dataId, fileName, EntityType.Contract, props.contractId);
            setDownloadingDocument("");
        } else {
            window.open(link, "_blank");
        }
    };

    function getFilterOptions(): Array<FilterOption> {
        const options: Array<FilterOption> = [
            {
                id: "name",
                label: translateString("name"),
                type: FilterOptionType.SelectField,
                value: selectFilters.name ?? [],
                fixedData: getUniqueFilterOptions(rawData, (d) => d.name).sort(sortByName),
            },
            {
                id: "type",
                label: translateString("type"),
                type: FilterOptionType.SelectField,
                value: selectFilters.type ?? [],
                fixedData: getUniqueFilterOptions(
                    rawData,
                    (d) => d.documentType.name,
                    (d) => d.documentType.id
                ).sort(sortByName),
            },
            {
                id: "status",
                label: translateString("status"),
                type: FilterOptionType.SelectField,
                value: selectFilters.status ?? [],
                fixedData: getUniqueFilterOptions(
                    rawData,
                    (d) => translateString(DocumentStatus[d.status].toLowerCase()),
                    (d) => d.status.toString()
                ).sort(sortByEnumValue),
            },
            {
                id: "owner",
                label: translateString("owner"),
                type: FilterOptionType.SelectField,
                value: selectFilters.owner ?? [],
                fixedData: getUniqueFilterOptions(
                    rawData,
                    (d) => d.owner.name,
                    (d) => d.owner.id
                ).sort(sortByName),
            },
            {
                id: "groupCompanies",
                label: translateString("groupCompanies"),
                type: FilterOptionType.SelectField,
                value: selectFilters.groupCompanies ?? [],
                fixedData: getGroupCompanyFilterData(),
            },
        ];

        if (platformFeatures.tags && permissions.canAccessTags) {
            options.push({
                id: "tags",
                label: translateString("tags"),
                type: FilterOptionType.SelectField,
                value: selectFilters.tags ?? [],
                fixedData: getUniqueNestedFilterOptions(
                    rawData,
                    (d) => d.tags,
                    (t) => t.name,
                    (t) => t.id
                ).sort(sortByName),
            });
        }

        if (showAiQualityAssurance) {
            options.push({
                id: "qualityScan",
                label: translateString("aiQualityScan"),
                type: FilterOptionType.Boolean,
                value: booleanFilters.qualityScan ?? "",
            });
        }

        return withOperators(options, filterOperators);
    }

    function getGroupCompanyFilterData(): Array<IdNamePair> {
        // Offering every group company until the contract has loaded narrows the picker down rather than filling it
        // up, so an option never appears to be missing while the request is in flight.
        const contractReachesAllGroupCompanies = contract?.allGroupEntityAccess ?? true;
        const contractGroupCompanies = (allGroupEntities ?? []).filter(
            (groupEntity) => contractReachesAllGroupCompanies || (contract?.groupEntities ?? []).includes(groupEntity.id)
        );

        // The documents only carry the group company names the user may be told, and for everyone but full contract
        // access those are already in the list above. It is full access this covers: it sees documents restricted to
        // group companies outside its own, which the group entity query never returns.
        const groupCompaniesOnDocuments = getUniqueNestedFilterOptions(
            rawData,
            (d) => d.groupEntityAccess,
            (groupEntity) => groupEntity.name,
            (groupEntity) => groupEntity.id
        );

        const selectableGroupCompanies = getUniqueFilterOptions(
            [...contractGroupCompanies, ...groupCompaniesOnDocuments],
            (groupEntity) => groupEntity.name,
            (groupEntity) => groupEntity.id
        ).sort(sortByName);

        return [{ id: appliesToAllGroupCompaniesFilterId, name: translateString("appliesToAllGroupCompanies") }, ...selectableGroupCompanies];
    }

    function filterDocuments(data: Array<ContractDocumentRow> | undefined) {
        return applyClientFilters(
            data,
            [
                { id: "name", candidate: (r) => r.name },
                { id: "type", candidate: (r) => r.documentType.id },
                { id: "status", candidate: (r) => r.status.toString() },
                { id: "owner", candidate: (r) => r.owner.id },
                { id: "tags", candidate: (r) => r.tags.map((t) => t.id) },
            ],
            selectFilters,
            {},
            filterOperators,
            (doc) => {
                const qualityScan = booleanFilters.qualityScan ?? "";
                if (qualityScan !== "") {
                    const hasResult = doc.aiQualityAssuranceResult != null;
                    if (qualityScan && !hasResult) return false;
                    if (!qualityScan && hasResult) return false;
                }
                // Matched on the complete id set, not the named subset: a document restricted only to group companies
                // the user cannot name would otherwise look unrestricted and match every selection.
                return matchesGroupCompanyFilter(selectFilters.groupCompanies ?? [], filterOperators.groupCompanies, doc.groupEntityAccessIds);
            }
        );
    }

    const hasActiveFilters =
        (selectFilters.name?.length ?? 0) > 0 ||
        (selectFilters.type?.length ?? 0) > 0 ||
        (selectFilters.status?.length ?? 0) > 0 ||
        (selectFilters.owner?.length ?? 0) > 0 ||
        (selectFilters.tags?.length ?? 0) > 0 ||
        (selectFilters.groupCompanies?.length ?? 0) > 0 ||
        (booleanFilters.qualityScan ?? "") !== "";

    // Two opens, two slots: the document goes onto the stage and the review into the rail beside it. The
    // document is already chosen here, so the review skips its picker and scans this one.
    function startAiUpdateFromDocument(document: ContractDocumentRow) {
        openPane(DocumentPane, {
            documents: [toPreviewableContractDocument(document)],
            initialDocumentId: document.documentVersionId,
        });
        openPane(ContractAiUpdatePane, { contractId: props.contractId, documentId: document.documentId });
    }

    function canStartAiUpdateFromDocument(document: ContractDocumentRow) {
        return aiUpdateAffordance.showContractAiUpdate && !isNullOrWhitespace(document.fileName) && isPreviewSupported(document.fileName ?? "");
    }

    // The same pairing as the AI-assisted update, with creation in the rail. The create pane owns neither the
    // document nor its closing until it has created the contract, so leaving it unfinished keeps the document open.
    function startCreateContractFromDocument(document: ContractDocumentRow) {
        const previewableDocument = toPreviewableContractDocument(document);
        openPane(DocumentPane, { documents: [previewableDocument], initialDocumentId: document.documentVersionId });
        openPane(CreateContractPane, { sourceDocument: { contractId: props.contractId, document: previewableDocument } });
    }

    // Archiving the document needs edit on this contract, which the AI update check already requires, and on its documents.
    function canCreateContractFromDocument(document: ContractDocumentRow) {
        return canStartAiUpdateFromDocument(document) && canCreateContracts && canEditDocuments;
    }

    function toPreviewableContractDocument(document: ContractDocumentRow): PreviewableDocument {
        return { ...document, entityType: EntityType.Contract, entityId: props.contractId, entityTitle: props.contractName };
    }

    // Blocked reasons are the plan's (add-on, spending, contract limit) and open the plan dialog. They come before
    // the disabled reason, which only says why the file cannot be used.
    function getAiUpdateBlockedTranslationKey() {
        return aiUpdateAffordance.blockedTranslationKey;
    }

    function getAiDisabledTranslationKey(document: ContractDocumentRow) {
        return document.isTooLargeForAi ? "textExtractionErrorFileTooLarge" : undefined;
    }

    function getCreateContractFromDocumentBlockedTooltip() {
        if (aiUpdateAffordance.blockedTranslationKey) {
            return translateString(aiUpdateAffordance.blockedTranslationKey);
        }

        if (contractsPlanLimitData?.limitReached) {
            return translateString("contractLimitReached", { count: contractManagementFeatures.maxNumberOfContracts });
        }

        return undefined;
    }

    function getAiDisabledTooltip(document: ContractDocumentRow) {
        const disabledTranslationKey = getAiDisabledTranslationKey(document);
        return disabledTranslationKey === undefined ? undefined : translateString(disabledTranslationKey);
    }

    return {
        canEditDocuments,
        canCreateContractFromDocument,
        startCreateContractFromDocument,
        getCreateContractFromDocumentBlockedTooltip,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        getAiUpdateBlockedTranslationKey,
        getAiDisabledTranslationKey,
        getAiDisabledTooltip,
        canStartAiUpdateFromDocument,
        startAiUpdateFromDocument,
        hasDocuments: isLoading || (rawData ?? []).length > 0,
        isLoading,
        documents,
        documentForAddOrEdit,
        setDocumentForAddOrEdit,
        documentForDeletion,
        setDocumentForDeletion,
        refetchDocuments,
        downloadingDocument,
        documentToSetAsMainDocument,
        setDocumentToSetAsMainDocument,
        onDownloadClick,
        showAuditLogDialog,
        setShowAuditLogDialog,
        getFilterOptions,
        onFilterChange,
        hasActiveFilters,
    };
}
